/*
 * GUARDIAN-CLAIM seat records (S2 FE slice, BOARDROOM-2026-09-03-052111 /
 * CMT-2026-09-03-020 — ratified child-flow rulings: the child is named by the
 * CHILD everywhere; the parent/guardian signs FOR the child).
 *
 * WHAT THIS IS: the device-local carrier of "this held seat was claimed AS a
 * parent/guardian for a named child". The S1 wire contract adds the guardian
 * facts ONLY to the /claim wires (camelCase claimKind + childName, GET lookup
 * and POST result) — no later read (/me/holdings, readiness) states them,
 * guardian holdings arriving kind='invitee'/holderName:null — so the claim
 * moment is the ONE place the FE learns the seat's guardian nature, and this
 * store persists that fact for the get-ready journey (guardian landing →
 * account-holder profile → child-confirm → guardian waiver → child-named QR
 * card). Mirrors the established localHolderOverlay/assignStore precedent:
 * device-local, honest about its limits, never presented as a server write.
 *
 * CHILD DOB + GENDER live here too: on the no-permission path they are NOT
 * captured at assign time — the PARENT is the authority and enters them in the
 * child-confirm step. SERVER-BACKED since my-api V25: ChildConfirm POSTs them
 * to /me/tickets/{id}/guardian-child (the server validates + stores them and
 * its age gates read them), and this record is only the CACHE — `confirmed`
 * is set ONLY after the server accepted; readiness.childDetailsComplete is the
 * truth the get-ready card prefers over it.
 *
 * DEGRADATION (honest — adversarial review P1, CMT-028 truthful-state
 * lineage): a guardian who claims on one device and gets ready on another has
 * no record here, and TODAY's /me/holdings wire carries NO guardian facts for
 * these rows (kind='invitee', holderName:null, no claimKind) — so cross-device
 * the card/wallet/waiver DISPLAY degrades to the parent-named session render.
 * Display-only: the server's participant/signer records stay correct. No
 * existing rail covers this; it closes only when the holdings wire carries
 * claimKind/childName (server follow-up S1.1, being built in parallel).
 */

const STORE_KEY = 'my.guardianClaims.v1'

export interface GuardianClaimRecord {
  /** The claimed ticket row — the join key to holdings/readiness. */
  ticketId: string
  eventId: string
  /** The child's name — seeded from the claim wire's child_name, superseded by
   *  the parent's own confirm/correction (the parent is the authority). May be
   *  '' when the wire carried none and the parent has not confirmed yet. */
  childName: string
  /** The parent-confirmed first / last name as entered in ChildConfirm's two
   *  fields (last may be '' — single-word names are legitimate, ruling 5 on
   *  #241). ABSENT until the first confirm (and on pre-split records): the
   *  form then pre-fills First name with the whole childName, never a guessed
   *  split. */
  childFirst?: string
  childLast?: string
  /** Child DOB select parts (wizardModel vocabulary: '4' / 'May' / '2016');
   *  '' until the parent enters them in the child-confirm step. */
  dobD: string
  dobM: string
  dobY: string
  /** Child gender pill value ('Female' | 'Male'); '' until chosen. */
  gender: string
  /** true once the parent has confirmed the child's details. */
  confirmed: boolean
  /** ISO timestamp of the claim on this device. */
  claimedAt: string
}

function readAll(): Record<string, GuardianClaimRecord> {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, GuardianClaimRecord>) : {}
  } catch {
    return {}
  }
}

function writeAll(records: Record<string, GuardianClaimRecord>): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(records))
  } catch {
    // Persistence unavailable (private mode etc.) — nothing silently invented;
    // the journey degrades to the wire-only rendering described above.
  }
}

/** Record a guardian claim at the claim moment (ClaimLanding, the one place
 *  the wire states it). Idempotent per ticket; a re-claim of the same ticket
 *  keeps an existing CONFIRMED record's child details (the parent's own input
 *  outranks a re-read of the wire seed). */
export function recordGuardianClaim(input: { ticketId: string; eventId: string; childName: string }): GuardianClaimRecord {
  const all = readAll()
  const existing = all[input.ticketId]
  if (existing?.confirmed) return existing
  const rec: GuardianClaimRecord = {
    ticketId: input.ticketId,
    eventId: input.eventId,
    childName: input.childName.trim(),
    dobD: existing?.dobD ?? '',
    dobM: existing?.dobM ?? '',
    dobY: existing?.dobY ?? '',
    gender: existing?.gender ?? '',
    confirmed: false,
    claimedAt: existing?.claimedAt ?? new Date().toISOString(),
  }
  all[input.ticketId] = rec
  writeAll(all)
  return rec
}

/** The guardian-claim record for a held ticket, or null (never fabricated). */
export function guardianClaimForTicket(ticketId: string): GuardianClaimRecord | null {
  return readAll()[ticketId] ?? null
}

/** true ⇔ this device recorded a guardian claim for the ticket — the gate for
 *  every guardian-journey behaviour change (self/purchaser flows untouched). */
export function isGuardianClaimedTicket(ticketId: string): boolean {
  return ticketId in readAll()
}

/** STALE-RECORD-GUARDED guardian gate (review sweep R4 — the PR#124 P1-2
 *  pattern GetReady applies at its own guardianClaimForTicket read, shared so
 *  ProfileWizard's contract filter applies the SAME rule): a device-local
 *  guardian record only APPLIES to a ticket whose wire-declared holding kind
 *  is NOT an explicit 'self'. An explicit kind='self' is the wire saying
 *  "this seat is the caller's own" (only ensureSelfHolder mints it; guardian-
 *  claimed seats ride invitee/child), so a lingering claim record for that
 *  row is stale (revoke → self-reassign) and must never flip a self seat to
 *  guardian treatment. `kind` absent/undefined (older my-api, no wire signal)
 *  keeps today's behaviour: the record applies. */
export function guardianRecordApplies(ticketId: string, kind?: import('./myApi').HolderKind): boolean {
  return kind !== 'self' && isGuardianClaimedTicket(ticketId)
}

/** CHILD-SUBJECT questionnaire ticket context (boardroom ruling 2026-10-10) —
 *  the ONE child-context rule shared by QuestionnaireFlow (route entry) and
 *  GetReady (per-card questionnaire status). A ticket is child-context when:
 *   - its holder kind is 'child', OR
 *   - the SERVER marks the holding childContext (/me/holdings — kind 'child'
 *     OR claim_kind 'guardian'; the fresh-device guardian case, where no
 *     device-local claim record exists), OR
 *   - a device-local guardian-claim record on the ticket applies
 *     (guardianRecordApplies — an explicit 'self' suppresses a stale record,
 *     so server kind 'self' with no childContext still wins).
 *  Not gated on a resolved child name. Returns the ticket to send as the BFF's
 *  ticket_id (the server keys the read/write on the child seat after its own
 *  ownership check), or undefined for an account (self) seat. */
export function questionnaireSubjectTicket(
  ticketId: string | null | undefined,
  holderKind: string | null | undefined,
  serverChildContext?: boolean | null,
): string | undefined {
  if (!ticketId) return undefined
  const childContext =
    holderKind === 'child' ||
    serverChildContext === true ||
    guardianRecordApplies(ticketId, (holderKind ?? undefined) as import('./myApi').HolderKind | undefined)
  return childContext ? ticketId : undefined
}

/** The server's child-context mark on a /me/holdings row: the explicit
 *  childContext carry, or — on a my-api that predates it — the guardian
 *  discriminator (claimKind 'guardian'), which is the same lane rule's
 *  guardian arm. kind 'child' is covered by the holder kind itself. */
export function holdingChildContext(
  h: Pick<import('./myApi').Holding, 'childContext' | 'claimKind'>,
): boolean {
  return h.childContext === true || h.claimKind === 'guardian'
}

/** Purge the record for a ticket (adversarial review P2): a LATER non-guardian
 *  claim of the same ticket (revoke → re-invite → self-claim) means the seat
 *  is no longer a guardian seat on this device — a lingering record would keep
 *  the child-named card, the childConfirm step and the suppressed eligibility
 *  gate on what is now a SELF seat. ClaimLanding calls this on every
 *  non-guardian claim success. */
export function removeGuardianClaim(ticketId: string): void {
  const all = readAll()
  if (!(ticketId in all)) return
  delete all[ticketId]
  writeAll(all)
}

/** The child-confirm step's write: the parent confirms/corrects the child's
 *  name and enters DOB + gender (the parent is the authority). */
export function confirmGuardianChild(
  ticketId: string,
  input: {
    childName: string
    childFirst?: string
    childLast?: string
    dobD: string
    dobM: string
    dobY: string
    gender: string
  },
): GuardianClaimRecord | null {
  const all = readAll()
  const existing = all[ticketId]
  if (!existing) return null
  const rec: GuardianClaimRecord = {
    ...existing,
    childName: input.childName.trim(),
    ...(input.childFirst !== undefined ? { childFirst: input.childFirst.trim() } : {}),
    ...(input.childLast !== undefined ? { childLast: input.childLast.trim() } : {}),
    dobD: input.dobD,
    dobM: input.dobM,
    dobY: input.dobY,
    gender: input.gender,
    confirmed: true,
  }
  all[ticketId] = rec
  writeAll(all)
  return rec
}

/** Test hygiene / future server-migration hook. */
export function clearGuardianClaims(): void {
  try {
    localStorage.removeItem(STORE_KEY)
  } catch {
    // nothing to clear
  }
}
